import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import {
  addJournalEntry,
  deleteJournalEntry,
  getJournalInsight,
  listJournalEntries,
  type JournalEntry,
  type JournalInsight,
} from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * AI Journal — user writes one entry per day. Priestess subscribers
 * get a weekly AI pattern insight refreshed every Monday.
 *
 * Layout:
 *   • Top: today's entry textarea (auto-loads if exists, replaces on save)
 *   • Middle: weekly insight card (Priestess) or upsell
 *   • Bottom: scrollable list of past entries with delete option
 */
export default function Journal() {
  const { t, i18n } = useTranslation();
  const { isPriestess } = useSubscription();

  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [insight, setInsight] = useState<JournalInsight | null>(null);
  const [draft, setDraft] = useState('');
  const [todayId, setTodayId] = useState<string | null>(null);
  // Last text the server confirmed — used to detect "clean saved" vs "dirty
  // edits since save". When draft === savedText the textarea is in saved
  // state (gold border, "✓ saved" label, button hidden until edits resume).
  const [savedText, setSavedText] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // "Saved" means: today's entry exists on the server AND the textarea
  // matches it verbatim. Any edit flips this back to dirty.
  const isSaved = todayId !== null && savedText !== null && draft === savedText;

  // Local YYYY-MM-DD — toISOString() returns UTC, which rolls over
  // 3 hours early for Moscow users (00:00 local = 21:00 UTC of "yesterday").
  // We use locale-aware formatting so the journal day matches the user's
  // clock, not the server's.
  const todayIso = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();
  const formatDate = (iso: string) =>
    new Date(`${iso}T12:00:00Z`).toLocaleDateString(
      i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US',
      { day: 'numeric', month: 'long' },
    );

  useEffect(() => {
    listJournalEntries(30)
      .then((list) => {
        setEntries(list);
        const today = list.find((e) => e.for_date === todayIso);
        if (today) {
          setDraft(today.interpretation);
          setTodayId(today.id);
          setSavedText(today.interpretation);
        }
      })
      .catch((e) => setError((e as Error).message));

    if (isPriestess) {
      getJournalInsight().then(setInsight).catch(() => {});
    }
  }, [isPriestess, todayIso]);

  const save = async () => {
    if (saving || draft.trim().length < 5) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await addJournalEntry(draft.trim());
      setTodayId(saved.id);
      setSavedText(saved.interpretation);
      // Snap the textarea to the canonical server text (in case the server
      // applied any normalisation — trim, etc.), so isSaved == true.
      setDraft(saved.interpretation);
      // Insert/replace today's entry in the local list so it appears immediately.
      setEntries((es) => {
        const without = es.filter((e) => e.for_date !== todayIso);
        return [{ ...saved }, ...without];
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string) => {
    try {
      await deleteJournalEntry(id);
      setEntries((es) => es.filter((e) => e.id !== id));
      if (id === todayId) {
        setDraft('');
        setTodayId(null);
        setSavedText(null);
      }
      setConfirmDelete(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        ✎ {t('journal.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('journal.subtitle')}
      </p>

      {/* Today's entry */}
      <OrnateFrame className="card-glow mb-4">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <p className={`text-[10px] uppercase tracking-widest transition ${
            isSaved ? 'text-gold-400/90' : 'text-gold-500/60'
          }`}>
            {isSaved
              ? t('journal.today_label_saved', { date: formatDate(todayIso) })
              : t('journal.today_label', { date: formatDate(todayIso) })}
          </p>
          {todayId && (
            <button
              type="button"
              onClick={() => setConfirmDelete(todayId)}
              className="text-[10px] uppercase tracking-widest
                         text-rose-400/70 transition hover:text-rose-400"
            >
              🗑 {t('journal.delete')}
            </button>
          )}
        </div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('journal.placeholder')}
          rows={6}
          maxLength={2000}
          className={`w-full resize-none rounded-lg border bg-night-900/60
                      px-3 py-2 font-serif text-base text-gold-100 outline-none
                      transition placeholder:text-gold-300/35
                      focus:border-gold-400 ${
                        isSaved
                          ? 'border-gold-500/70 shadow-[0_0_18px_-6px_rgba(232,195,86,0.45)]'
                          : 'border-gold-500/30'
                      }`}
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-[10px] text-gold-300/40">
            {draft.length}/2000
          </span>
          {(!isSaved || saving) && (
            <button
              type="button"
              onClick={save}
              disabled={saving || draft.trim().length < 5}
              className="rounded-lg border border-gold-500
                         bg-gold-500/20 px-4 py-1.5 text-sm text-gold-100
                         transition hover:bg-gold-500/30 disabled:opacity-40"
            >
              {saving
                ? t('journal.saving')
                : todayId
                  ? t('journal.update')
                  : t('journal.save')}
            </button>
          )}
        </div>
        {isSaved && !saving && (
          <p className="mt-2 text-[11px] italic leading-snug text-gold-300/60">
            {t('journal.editable_hint')}
          </p>
        )}
        {savedFlash && (
          <p className="mt-2 text-center text-xs text-gold-400/80">
            ✓ {t('journal.saved')}
          </p>
        )}
        {error && (
          <p className="mt-2 text-center text-xs text-rose-400">{error}</p>
        )}
      </OrnateFrame>

      {/* Weekly insight (Priestess) */}
      {isPriestess && insight && (
        <OrnateFrame className="card-glow mb-4 border-gold-500/50">
          <p className="mb-2 text-[10px] uppercase tracking-[0.25em] text-gold-500/70">
            ✦ {t('journal.insight_title')}
          </p>
          {insight.stub ? (
            <p className="font-serif italic leading-relaxed text-gold-300/70">
              {insight.interpretation}
            </p>
          ) : (
            <RichText
              text={insight.interpretation}
              className="font-serif italic leading-relaxed text-gold-200"
            />
          )}
        </OrnateFrame>
      )}

      {!isPriestess && (
        <OrnateFrame className="card-glow mb-4 border-gold-500/40">
          <p className="mb-1 text-center text-[10px] uppercase tracking-[0.25em] text-gold-500/70">
            👑 {t('journal.insight_locked_label')}
          </p>
          <h3 className="mb-2 text-center font-serif text-lg text-gold-200">
            {t('journal.insight_locked_title')}
          </h3>
          <p className="mb-4 text-center text-xs italic leading-relaxed text-gold-300/70">
            {t('journal.insight_locked_blurb')}
          </p>
          <Link to="/profile" className="btn-primary block w-full text-center">
            {t('journal.insight_locked_cta')}
          </Link>
        </OrnateFrame>
      )}

      {/* Past entries */}
      <div className="mt-6">
        <p className="mb-3 text-center text-[10px] uppercase tracking-widest text-gold-500/60">
          {t('journal.past_title')}
        </p>
        {entries.filter((e) => e.for_date !== todayIso).length === 0 && (
          <p className="text-center text-xs italic text-gold-300/50">
            {t('journal.past_empty')}
          </p>
        )}
        <div className="space-y-2">
          {entries
            .filter((e) => e.for_date !== todayIso)
            .map((e) => (
              <OrnateFrame key={e.id} className="card-glow">
                <div className="mb-1 flex items-baseline justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-gold-500/60">
                    {formatDate(e.for_date)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(e.id)}
                    className="text-[10px] uppercase tracking-widest
                               text-rose-400/70 transition hover:text-rose-400"
                  >
                    🗑 {t('journal.delete')}
                  </button>
                </div>
                <p className="whitespace-pre-line font-serif text-sm leading-relaxed text-gold-200">
                  {e.interpretation}
                </p>
              </OrnateFrame>
            ))}
        </div>
      </div>

      {/* Delete confirmation */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center
                       bg-night-950/80 backdrop-blur-sm px-6"
            onClick={() => setConfirmDelete(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-sm rounded-3xl border border-rose-400/30
                         bg-night-800/95 px-6 py-6 text-center"
            >
              <p className="mb-1 text-3xl">🗑</p>
              <h3 className="mb-2 font-serif text-xl text-gold-200">
                {t('journal.delete_confirm_title')}
              </h3>
              <p className="mb-5 text-sm italic leading-relaxed text-gold-300/70">
                {t('journal.delete_confirm_body')}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="flex-1 rounded-lg border border-gold-500/30 py-2.5
                             text-sm text-gold-300/80 hover:border-gold-400"
                >
                  {t('journal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(confirmDelete)}
                  className="flex-1 rounded-lg border border-rose-400/60
                             bg-rose-500/20 py-2.5 text-sm text-rose-200
                             hover:bg-rose-500/30"
                >
                  {t('journal.delete')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
